/*
 https://wavesaudio.atlassian.net/browse/DSWEB-1185

 There are 2 campaign types
 ----------------------------
 1. TotalTiers, CampaignTypeID = 3, if user purchases > given amount -> receives free plugins

 2. MultiPlugin, CampaignTypeID = 5, if user purchases > given number of PLUGINS -> receives free plugins

 There are 2 types of controls:
 ------------------------------
 1. Green confirmation message in the first step below the 'Order Total'
 2. Popup that appears when user moves from step 1 to step 2
    Each campaign type has its own popup (with different UI)
*/

import { ICartData } from '../interfaces';
import { FreePluginEnum } from '../enums/free-plugins-enum';
import { ProductDepartmentEnum } from '../../../products/enums/product-department-enum';
import { getSelectedCartItems } from '../data/data-provider';

/**
 * get plugins number including qty
 */
const getPluginsNumber = (cartData: ICartData) => {
    return cartData.cartItems.reduce(
        (count, cartItem) => (cartItem.skuDepartmentID === ProductDepartmentEnum.Plugins) && cartItem.isActive ?
        count + cartItem.skuUnits :
        count,
    0);
};

/**
 * Check if campaign data saved in the store is valid.
 * It should be a non-empty array.
 */
const isCampaignDataValid = (cartData: ICartData) => {
    return cartData && cartData.efsData && Array.isArray(cartData.efsData) && cartData.efsData.length > 0;
};

/**
 * if campaign type = TotalTiers, CampaignTypeID = 3 - used for the green confirmation
 * check if user eligible to get the free plugins
 */
const isTotalTiersEligible = (cartData: ICartData) => {
    if(!cartData.efsData || cartData.efsData.length <= 0) return false;

    const efsData = [...cartData.efsData];
    efsData.sort((item1, item2) => {
        return item1.cap - item2.cap;
    });

    const cap = Number(efsData[0].cap) || 0;
    const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

    if(subtotalWithoutDiscount < cap) return false;

    const activeCartItems = getSelectedCartItems(cartData);
    
    for (let i = 0; i < activeCartItems.length; i++) {
        const cartItem = activeCartItems[i];

        if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Software &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Courses &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.CoursesWithSoftware) {
            return false;
        }
    }

    return true;
};

/**
 * If campaign type = MultiPlugin, CampaignTypeID = 5,
 * check if user eligible to get the free plugins.
 */
const isMultiPluginEligible = (cartData: ICartData) => {
    if(!cartData.efsData || cartData.efsData.length <= 0) return false;

    const minNumberOfPurchasedPlugins = Number(cartData.efsData[0].minNumberOfPurchasedPlugins) || 0;
    const pluginsCount = getPluginsNumber(cartData);
    return pluginsCount >= minNumberOfPurchasedPlugins;
};

/**
 * Check if green confirmation message in the first step below the 'Order Total' is visible.
 */
export const isConfirmationMsgVisible = (cartData: ICartData) => {

    // the campaign data should exist and be non empty
    if(!isCampaignDataValid(cartData)) return false;

    const campaignID = Number(cartData.efsCampaignTypeID) || 0;
    if(campaignID === 0) return false;

    switch (campaignID){

        // TotalTiers, CampaignTypeID = 3, if user purchases > given amount -> receives free plugins
        case FreePluginEnum.TotalTiers: {
            return isTotalTiersEligible(cartData);
        }

        // MultiPlugin, CampaignTypeID = 5, if user purchases > given number of products -> receives free plugins
        case FreePluginEnum.MultiPlugin: {
            return isMultiPluginEligible(cartData);
        }
    }

    return false;
};

const getSelectedTotalTierIndex = (cartData: ICartData) => {

    const campaignData = cartData.efsData;
    const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

    for(let i=0; i<campaignData.length; i++){
        const item = campaignData[i];

        if(i === campaignData.length - 1 && subtotalWithoutDiscount >= item.cap) return i;
        if(subtotalWithoutDiscount >= item.cap && subtotalWithoutDiscount < item.upperCap) return i;
    }

    return -1;
};

export const getNumberOfFreePlugins = (cartData: ICartData) => {
    const campaignID = Number(cartData.efsCampaignTypeID) || 0;

    if(campaignID === FreePluginEnum.TotalTiers){
        const selectedTierIndex = getSelectedTotalTierIndex(cartData);
        if(selectedTierIndex !== -1){
            return cartData.efsData[selectedTierIndex].numberOfFreePlugins;
        }
    }

    return cartData.efsData[0].numberOfFreePlugins;
};
